import { templateLabel } from './templates.js'

// What a search looks through, and what it suggests, worked out from the resumes already on the page (no request).
// Each entry is { kind, text } where kind says what the text is: a resume, a job it was written for, a company, a skill, a template, a model.

const clean = (value) => (typeof value === 'string' ? value.trim() : '')
const list = (value) => (Array.isArray(value) ? value : [])

// The things one resume can be found by, each with its kind.
export function entriesFor(resume) {
  const content = resume.content || {}
  const entries = [{ kind: 'Resume', text: clean(resume.name), ai: Boolean(resume.ai_generated) }]
  const forJob = clean(resume.generated_for) || clean(resume.job?.title)
  // "Senior Engineer at Acme": the company is the name kept with the job, or what follows the last " at " while the job is gone.
  const company = clean(resume.job?.company_name) || clean(forJob.split(/\s+at\s+/i).slice(1).pop())
  if (forJob) entries.push({ kind: 'Written for', text: forJob, company, logo: resume.job?.company_logo_url || null, ai: Boolean(resume.ai_generated) })
  if (clean(resume.headline)) entries.push({ kind: 'Headline', text: clean(resume.headline) })
  list(content.experience).forEach((job) => entries.push({ kind: 'Company', text: clean(job.company), company: clean(job.company) }))
  list(content.skills).forEach((group) => {
    entries.push({ kind: 'Skill', text: clean(group.name) })
    list(group.items).forEach((item) => entries.push({ kind: 'Skill', text: clean(item) }))
  })
  list(content.education).forEach((school) => entries.push({ kind: 'School', text: clean(school.school) }))
  entries.push({ kind: 'Template', text: templateLabel(resume.template) })
  if (resume.generation?.model) entries.push({ kind: 'Model', text: resume.generation.model })
  return entries.filter((entry) => entry.text)
}

const words = (query) => clean(query).toLowerCase().split(/\s+/).filter(Boolean)

// A resume matches when every word of the query is in something it can be found by (and in its person's name).
export function matches(resume, query) {
  const needed = words(query)
  if (needed.length === 0) return true

  const haystack = [...entriesFor(resume).map((entry) => entry.text), clean(resume.content?.basics?.full_name), resume.ai_generated ? 'ai generated' : '']
    .join(' \n ').toLowerCase()
  return needed.every((word) => haystack.includes(word))
}

export const filterResumes = (resumes, query) => resumes.filter((resume) => matches(resume, query))

const PER_KIND = 3
const TOTAL = 8
const ORDER = ['Resume', 'Written for', 'Company', 'Skill', 'Headline', 'School', 'Template', 'Model']

// Suggestions for what has been typed so far: values from the resumes that contain the typed text, the ones that start with it first,
// grouped by kind. With nothing typed, the user's resumes.
export function suggestions(resumes, query) {
  const typed = clean(query).toLowerCase()
  const seen = new Set()
  const pool = []

  resumes.forEach((resume) => {
    entriesFor(resume).forEach((entry) => {
      const lower = entry.text.toLowerCase()
      const key = `${entry.kind}|${lower}`
      if (seen.has(key) || (typed && !lower.includes(typed))) return
      if (!typed && entry.kind !== 'Resume') return
      seen.add(key)
      pool.push({ ...entry, starts: typed ? lower.startsWith(typed) : false })
    })
  })

  return ORDER.flatMap((kind) => pool
    .filter((entry) => entry.kind === kind)
    .sort((a, b) => Number(b.starts) - Number(a.starts) || a.text.localeCompare(b.text))
    .slice(0, typed ? PER_KIND : TOTAL))
    .slice(0, TOTAL)
}
